import React, { useState, Component } from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { buildBreadcrumbList, buildFAQSchema, buildServiceSchema } from '../lib/structuredData';
import { ArrowRightIcon, SearchIcon, ZapIcon, FileTextIcon, MapPinIcon, TrendingUpIcon, ImageIcon, BrainIcon, GlobeIcon, CheckCircleIcon, BarChartIcon, LinkIcon, CodeIcon, SettingsIcon, LayersIcon, MousePointerClickIcon, DatabaseIcon, WrenchIcon, RocketIcon, RefreshCwIcon, TargetIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, Share2Icon, LayoutTemplateIcon, NetworkIcon, SmartphoneIcon, MessageSquareIcon, PlayCircleIcon, AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export const SEOPage: React.FC = () => {
  const [expandedComponent, setExpandedComponent] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  // --- DATA STRUCTURES ---
  const seoFactors = [{
    icon: <WrenchIcon className="w-6 h-6" data-id="element-1963" data-mp="w5il8ffq9t8t" />,
    title: 'Techniek',
    desc: 'De fundering. Als Google je site niet kan lezen, houdt het op.'
  }, {
    icon: <FileTextIcon className="w-6 h-6" data-id="element-1964" data-mp="m8dstxvah1jk" />,
    title: 'Content',
    desc: 'Het antwoord op de vraag van je klant. Relevant en diepgaand.'
  }, {
    icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-1965" data-mp="rabk9fduf2nl" />,
    title: 'Autoriteit',
    desc: 'Vertrouwen. Andere sites die naar jou verwijzen als expert.'
  }, {
    icon: <MousePointerClickIcon className="w-6 h-6" data-id="element-1966" data-mp="i8vifw13g8uz" />,
    title: 'UX',
    desc: 'Gebruiksvriendelijkheid. Een bezoeker moet makkelijk navigeren.'
  }, {
    icon: <Share2Icon className="w-6 h-6" data-id="element-1967" data-mp="8i0lnxq81qva" />,
    title: 'Distributie',
    desc: 'Hoe je content verspreid wordt over verschillende kanalen.'
  }];
  const maturityPhases = [{
    phase: 'Basis',
    levels: '0-2',
    title: 'De Fundering',
    happening: 'Website staat live, maar trekt nauwelijks relevant verkeer. Losse blogs zonder strategie.',
    missing: 'Technische basis, logische structuur en gerichte zoekwoorden.',
    color: 'bg-gray-100 border-gray-200 text-gray-800'
  }, {
    phase: 'Groei',
    levels: '3-5',
    title: 'De Structuur',
    happening: "Verkeer neemt toe. Pagina's ranken op specifieke termen. Leads beginnen binnen te komen.",
    missing: 'Silo-structuur, interne linking en schaalbare content.',
    color: 'bg-blue-50 border-blue-200 text-[#007AFF]'
  }, {
    phase: 'Dominantie',
    levels: '6-9',
    title: 'Het Systeem',
    happening: 'Je bent de autoriteit in jouw niche. Verkeer is voorspelbaar en schaalbaar. Conversies zijn hoog.',
    missing: 'Niets. Het is nu een kwestie van uitbouwen en domineren.',
    color: 'bg-[#1B2B44] border-[#1B2B44] text-white'
  }];
  const seoComponents = [{
    id: 'technisch',
    title: 'Technische SEO',
    icon: <CodeIcon className="w-5 h-5" data-id="element-1968" data-mp="ypon8hks79mq" />,
    explanation: 'De motor van je website. Zonder goede techniek kan Google je content niet vinden of begrijpen.',
    happens: 'We optimaliseren laadtijden (Core Web Vitals), fixen dode links, verbeteren de mobiele weergave en zorgen voor een perfecte indexatie.',
    matters: 'Als de techniek faalt, is alle andere moeite voor niets. Een snelle, foutloze site is een harde eis van Google.',
    blogLink: '/seo/blog/technische-seo-uitleg',
    blogTitle: 'Wat is technische SEO?'
  }, {
    id: 'onpage',
    title: 'On-page SEO',
    icon: <LayoutTemplateIcon className="w-5 h-5" data-id="element-1969" data-mp="3p3zo8newni53" />,
    explanation: "Het optimaliseren van individuele pagina's zodat ze perfect aansluiten bij wat de zoeker wil zien.",
    happens: 'We structureren titels (H1, H2), optimaliseren meta-data, verbeteren de leesbaarheid en zorgen voor de juiste zoekintentie.',
    matters: 'Dit vertelt Google exact waar de pagina over gaat en overtuigt de bezoeker om te klikken in de zoekresultaten.',
    blogLink: '/seo/blog/hoe-optimaliseer-je-een-pagina',
    blogTitle: 'Hoe optimaliseer je een pagina voor Google?'
  }, {
    id: 'architectuur',
    title: 'Site Architectuur',
    icon: <NetworkIcon className="w-5 h-5" data-id="element-1970" data-mp="1dhnrwnxfrskk" />,
    explanation: "De manier waarop je website is opgebouwd en hoe pagina's met elkaar verbonden zijn.",
    happens: 'We bouwen een logische hiërarchie (Silo structuur) en implementeren een slimme interne linkstrategie.',
    matters: 'Een goede structuur verdeelt de "SEO-waarde" over je hele site en helpt bezoekers (en Google) de weg te vinden.',
    blogLink: '/seo/blog/waarom-interne-linking-cruciaal-is',
    blogTitle: 'Waarom interne linking cruciaal is'
  }, {
    id: 'content',
    title: 'Content Strategie',
    icon: <FileTextIcon className="w-5 h-5" data-id="element-1971" data-mp="14gpitr854uv4" />,
    explanation: 'Het plan achter wat je schrijft. Geen losse flodders, maar een doordacht systeem van artikelen.',
    happens: 'We creëren topic clusters: één hoofdpagina (pillar) met daaromheen ondersteunende artikelen die allemaal naar elkaar linken.',
    matters: 'Dit bouwt topical authority op. Je laat zien dat je dé expert bent over een heel onderwerp, niet slechts één zoekwoord.',
    blogLink: '/seo/blog/zoekintentie-uitleg',
    blogTitle: 'Wat is zoekintentie?'
  }, {
    id: 'autoriteit',
    title: 'Autoriteit (Off-page)',
    icon: <LinkIcon className="w-5 h-5" data-id="element-1972" data-mp="28q9511ii374j" />,
    explanation: 'Hoe de rest van het internet naar jouw website kijkt. Ben je een betrouwbare bron?',
    happens: 'We verzamelen kwalitatieve links (backlinks) van andere relevante, betrouwbare websites naar jouw site.',
    matters: 'Backlinks werken als "stemmen". Hoe meer kwalitatieve stemmen, hoe hoger Google je waardeert.',
    blogLink: '/seo/blog/hoe-bouw-je-autoriteit-op',
    blogTitle: 'Hoe bouw je autoriteit op?'
  }, {
    id: 'lokaal',
    title: 'Local SEO',
    icon: <MapPinIcon className="w-5 h-5" data-id="element-1973" data-mp="2wcpj0ktd74m3" />,
    explanation: 'Gevonden worden door mensen in jouw directe omgeving die nu een oplossing zoeken.',
    happens: 'We optimaliseren je Google Bedrijfsprofiel, Apple Business Connect en zorgen voor lokale vermeldingen.',
    matters: 'Voor veel adviseurs komen de beste leads uit de eigen regio. Local SEO zorgt dat jij bovenaan staat op de kaart.',
    blogLink: '/seo/blog/local-seo-uitleg',
    blogTitle: 'Wat is local SEO en hoe word je lokaal vindbaar?'
  }, {
    id: 'ux',
    title: 'UX & Conversie',
    icon: <MousePointerClickIcon className="w-5 h-5" data-id="element-1974" data-mp="38xwedg1noc7s" />,
    explanation: 'De gebruikerservaring. Wat doet een bezoeker zodra hij op je site landt?',
    happens: 'We analyseren gedrag, verbeteren de navigatie, versnellen het proces en plaatsen duidelijke call-to-actions.',
    matters: 'Verkeer zonder conversie is waardeloos. Een goede UX houdt bezoekers langer vast (goed voor SEO) en levert leads op.',
    blogLink: '/seo/blog/wat-is-ux-en-waarom-beinvloedt-het-seo',
    blogTitle: 'Wat is UX en waarom beïnvloedt het SEO?'
  }, {
    id: 'ai',
    title: 'Entity & AI SEO',
    icon: <BrainIcon className="w-5 h-5" data-id="element-1975" data-mp="o75gab9s8xma" />,
    explanation: 'Optimaliseren voor hoe moderne (AI) zoekmachines de wereld begrijpen in concepten, niet alleen in woorden.',
    happens: 'We gebruiken structured data (Schema markup) om entiteiten (personen, bedrijven, diensten) keihard te definiëren voor Google.',
    matters: 'Zoekmachines worden slimmer. Ze zoeken naar de betekenis achter woorden. Wie dit goed doet, domineert de toekomst.',
    blogLink: '/seo/blog/wat-is-entity-seo',
    blogTitle: 'Wat is entity SEO?'
  }, {
    id: 'distributie',
    title: 'Distributie',
    icon: <Share2Icon className="w-5 h-5" data-id="element-1976" data-mp="dreip14k3m2u" />,
    explanation: 'Hoe je content bij de juiste mensen krijgt, buiten Google om.',
    happens: 'We zorgen dat content makkelijk deelbaar is en hergebruikt kan worden voor nieuwsbrieven en social media.',
    matters: 'SEO is traag, distributie is snel. Samen zorgen ze voor een constante stroom van bezoekers.',
    blogLink: '/seo/blog/seo-vs-advertenties',
    blogTitle: 'SEO vs advertenties: wat is het verschil?'
  }, {
    id: 'visueel',
    title: 'Visual & Media SEO',
    icon: <ImageIcon className="w-5 h-5" data-id="element-1977" data-mp="2o7ivnpx4u9wb" />,
    explanation: "Het vindbaar maken van je afbeeldingen, video's en documenten.",
    happens: "We optimaliseren bestandsnamen, alt-teksten, compressie en video-schema's.",
    matters: "Mensen zoeken steeds vaker visueel. Bovendien maken geoptimaliseerde media je gewone pagina's sterker.",
    blogLink: '/seo/blog/wat-is-technische-seo',
    blogTitle: 'Wat is technische SEO?'
  }, {
    id: 'data',
    title: 'Data & Optimalisatie',
    icon: <BarChartIcon className="w-5 h-5" data-id="element-1978" data-mp="32wx6l2gcwzdt" />,
    explanation: 'Meten is weten. We sturen op harde data, niet op onderbuikgevoel.',
    happens: 'We monitoren rankings, klikfrequenties (CTR) en conversies via Google Search Console en Analytics.',
    matters: 'Zonder data ben je blind. Door te meten kunnen we continu bijsturen en het systeem verbeteren.',
    blogLink: '/seo/blog/hoe-lang-duurt-seo',
    blogTitle: 'Hoe lang duurt SEO voordat je resultaat ziet?'
  }, {
    id: 'trust',
    title: 'Trust & Legal',
    icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-1979" data-mp="3t6fjqa0asxwo" />,
    explanation: 'E-E-A-T: Experience, Expertise, Authoritativeness, Trustworthiness.',
    happens: 'We voegen auteursprofielen toe, tonen keurmerken, reviews en zorgen voor transparante voorwaarden.',
    matters: 'Zeker in de financiële sector (YMYL, oftewel Your Money or Your Life) eist Google extreme betrouwbaarheid voordat ze je tonen.',
    blogLink: '/seo/blog/waarom-werkt-seo-vaak-niet',
    blogTitle: 'Waarom werkt SEO vaak niet?'
  }];
  const formulas = [{
    title: 'De Ranking Formule',
    formula: 'Ranking = Relevantie × Autoriteit × Vertrouwen'
  }, {
    title: 'De Groei Formule',
    formula: 'SEO groei = (Content × Structuur) × Tijd'
  }, {
    title: 'De Traffic Formule',
    formula: 'Traffic = Ranking × CTR'
  }, {
    title: 'De Conversie Formule',
    formula: 'Conversie = Traffic × Vertrouwen × Duidelijkheid'
  }, {
    title: 'De Dominantie Formule',
    formula: 'Dominantie = Silo × Content × Distributie × Tijd'
  }];
  const faqs = [{
    q: 'Wat is SEO precies?',
    a: 'SEO (Search Engine Optimization) is het proces van het optimaliseren van je website zodat deze beter vindbaar is in zoekmachines zoals Google. Het is geen eenmalig trucje, maar een systeem van techniek, content, autoriteit en gebruikerservaring dat samenwerkt om structureel relevante bezoekers aan te trekken.'
  }, {
    q: 'Hoe lang duurt SEO?',
    a: 'SEO is een lange-termijn strategie. Meestal zie je de eerste voorzichtige resultaten na 3 tot 6 maanden. Echte, structurele groei en dominantie in jouw markt kost vaak 6 tot 12 maanden. Dit hangt af van de concurrentie, de huidige staat van je website en de inzet die we leveren. Het is een investering die zich op de lange termijn exponentieel terugbetaalt.'
  }, {
    q: 'Waarom werkt SEO vaak niet?',
    a: 'SEO faalt meestal door een gebrek aan structuur of focus. Veel bedrijven schrijven losse blogs zonder onderlinge samenhang (geen silo-structuur), hebben een trage website, of richten zich op zoekwoorden waar hun doelgroep niet op zoekt. SEO werkt alleen als alle factoren (Techniek, Content, Autoriteit, UX) kloppen. Als één factor nul is, is het resultaat nul.'
  }, {
    q: 'Wat is een SEO silo?',
    a: 'Een SEO silo is een manier om je website logisch te structureren. In plaats van alle pagina\'s los naast elkaar te zetten, groepeer je gerelateerde content. Je hebt een hoofdpagina (bijv. "Hypotheekadvies") met daaronder subpagina\'s (bijv. "Hypotheek oversluiten", "Startershypotheek"). Deze linken strategisch naar elkaar. Dit maakt je site overzichtelijk voor bezoekers én Google begrijpt direct waar je expert in bent.'
  }, {
    q: 'Heb ik blogs nodig?',
    a: 'Ja, maar niet zomaar "blogs". Je hebt strategische content nodig die antwoord geeft op de vragen van je doelgroep. We noemen dit liever een kennisbank of topic clusters. Deze informatieve artikelen trekken mensen aan in de oriëntatiefase en bouwen jouw autoriteit op. Vervolgens leiden we deze bezoekers via interne links naar je commerciële dienstpagina\'s.'
  }, {
    q: 'Is SEO beter dan advertenties?',
    a: 'Ze dienen een ander doel. Advertenties (SEA) leveren direct verkeer op, maar zodra je stopt met betalen, stopt het verkeer. SEO duurt langer om op te bouwen, maar levert daarna structureel, "gratis" verkeer op. Voor duurzame groei is SEO essentieel. Vaak is een combinatie in het begin het sterkst: advertenties voor de korte termijn, SEO voor de lange termijn.'
  }, {
    q: 'Wat kost SEO?',
    a: 'SEO is geen standaard pakketje met een vaste prijs. De investering hangt af van jouw doelen, de huidige staat van je website en de concurrentie in jouw markt. We kijken eerst naar wat er nodig is om jouw doelen te bereiken en maken op basis daarvan een strategisch plan. Zie het niet als een kostenpost, maar als een investering in een structureel verkoopkanaal.'
  }];
  const blogClusters = [{
    title: 'SEO Fundamentals',
    articles: [{
      title: 'Wat is SEO en hoe werkt het?',
      slug: 'wat-is-seo-en-hoe-werkt-het'
    }, {
      title: 'Waarom werkt SEO vaak niet?',
      slug: 'waarom-werkt-seo-vaak-niet'
    }, {
      title: 'Hoe lang duurt SEO voordat je resultaat ziet?',
      slug: 'hoe-lang-duurt-seo'
    }, {
      title: 'SEO vs advertenties: wat is het verschil?',
      slug: 'seo-vs-advertenties'
    }]
  }, {
    title: 'SEO Silo (Core)',
    articles: [{
      title: 'Wat is een SEO silo en hoe werkt het?',
      slug: 'seo-silo-wat-is-het'
    }, {
      title: 'Hoe bouw je een SEO silo structuur?',
      slug: 'seo-silo-opzetten'
    }, {
      title: 'Waarom interne linking cruciaal is',
      slug: 'waarom-interne-linking-cruciaal-is'
    }, {
      title: 'Veelgemaakte fouten in website structuur',
      slug: 'veelgemaakte-fouten-website-structuur'
    }, {
      title: 'Hoe Google websites begrijpt (simpel uitgelegd)',
      slug: 'hoe-google-websites-begrijpt'
    }]
  }, {
    title: 'Technical SEO',
    articles: [{
      title: 'Wat is technische SEO?',
      slug: 'technische-seo-uitleg'
    }, {
      title: 'Wat zijn Core Web Vitals?',
      slug: 'wat-zijn-core-web-vitals'
    }, {
      title: 'Hoe werkt indexatie in Google?',
      slug: 'hoe-werkt-indexatie'
    }, {
      title: 'Wat doet een XML sitemap?',
      slug: 'wat-doet-een-xml-sitemap'
    }, {
      title: 'Wat zijn canonicals en waarom zijn ze belangrijk?',
      slug: 'wat-zijn-canonicals'
    }]
  }, {
    title: 'Content SEO',
    articles: [{
      title: 'Hoe schrijf je SEO teksten?',
      slug: 'hoe-schrijf-je-seo-teksten'
    }, {
      title: 'Wat is zoekintentie?',
      slug: 'zoekintentie-uitleg'
    }, {
      title: 'Hoe kies je de juiste keywords?',
      slug: 'hoe-kies-je-keywords'
    }, {
      title: 'Wat is het verschil tussen blogs en landingspagina’s?',
      slug: 'blogs-vs-landingspaginas'
    }, {
      title: 'Hoe optimaliseer je een pagina voor Google?',
      slug: 'hoe-optimaliseer-je-een-pagina'
    }]
  }, {
    title: 'Local SEO',
    articles: [{
      title: 'Wat is local SEO?',
      slug: 'local-seo-uitleg'
    }, {
      title: 'Hoe werkt Google Business?',
      slug: 'hoe-werkt-google-business'
    }, {
      title: 'Wat is Apple Business Connect?',
      slug: 'wat-is-apple-business-connect'
    }, {
      title: 'Hoe word je lokaal vindbaar in jouw regio?',
      slug: 'lokaal-vindbaar-worden'
    }]
  }, {
    title: 'Authority & Backlinks',
    articles: [{
      title: 'Wat zijn backlinks?',
      slug: 'wat-zijn-backlinks'
    }, {
      title: 'Hoe bouw je autoriteit op?',
      slug: 'hoe-bouw-je-autoriteit-op'
    }, {
      title: 'Wat is het verschil tussen goede en slechte links?',
      slug: 'goede-vs-slechte-links'
    }]
  }, {
    title: 'Programmatic SEO',
    articles: [{
      title: 'Wat is programmatic SEO?',
      slug: 'wat-is-programmatic-seo'
    }, {
      title: 'Hoe schaal je naar 100+ pagina’s?',
      slug: 'hoe-schaal-je-paginas'
    }, {
      title: 'Hoe voorkom je duplicate content?',
      slug: 'voorkom-duplicate-content'
    }]
  }, {
    title: 'AI & Entity SEO',
    articles: [{
      title: 'Wat is entity SEO?',
      slug: 'wat-is-entity-seo'
    }, {
      title: 'Hoe werkt SEO voor AI tools zoals ChatGPT?',
      slug: 'seo-voor-ai-tools'
    }, {
      title: 'Wat is structured data?',
      slug: 'wat-is-structured-data'
    }]
  }, {
    title: 'UX & Conversie',
    articles: [{
      title: 'Wat is UX en waarom beïnvloedt het SEO?',
      slug: 'wat-is-ux-en-waarom-beinvloedt-het-seo'
    }, {
      title: 'Hoe verhoog je conversie op je website?',
      slug: 'hoe-verhoog-je-conversie'
    }, {
      title: 'Waarom verlaten bezoekers je website?',
      slug: 'waarom-verlaten-bezoekers-website'
    }]
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1980" data-mp="3rmryip4fcbbn">
      <SEO title="SEO in Noord-Holland Noord, structureel beter gevonden | Noord Digital" description="SEO is geen los trucje maar een systeem van techniek, content en autoriteit. Zo zorgen we dat ondernemers in Noord-Holland Noord gevonden worden op wat hun klanten echt zoeken." keywords="seo Noord-Holland Noord, zoekmachine optimalisatie Alkmaar, lokale seo, beter gevonden in Google, seo voor mkb, silo-structuur seo" canonical="https://noorddigital.nl/seo" jsonLd={[buildServiceSchema({
      name: 'SEO',
      description: 'Zoekmachine optimalisatie voor dienstverlenende bedrijven in Noord-Holland Noord, gericht op de zoekvragen die echt aanvragen opleveren.',
      path: '/seo',
      serviceType: 'Zoekmachine optimalisatie'
    }), buildBreadcrumbList([{
      name: 'SEO',
      path: '/seo'
    }]), buildFAQSchema(faqs.map(faq => ({
      question: faq.q,
      answer: faq.a
    })))]} data-id="element-1981" data-mp="ffgk7vpvtuo" />
      <Header data-id="element-1982" data-mp="2mnldtylpkhwm" />

      {/* 1. HERO SECTION */}
      <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-20 md:py-28 relative overflow-hidden" data-id="element-1983" data-mp="2moeohansfrk1">
        <div className="absolute inset-0 opacity-10" data-id="element-1984" data-mp="2inuq63rhahwb">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-1985" data-mp="lk2pnx2g6e3v"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-1986" data-mp="clha8as9vf7b">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-1987" data-mp="hkagpj9565xz">
            <div className="lg:col-span-7" data-id="element-1988" data-mp="158l7tup1cr92">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 mb-8" data-id="element-1989" data-mp="159mtsu7lay5k">
                <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-8 h-8 rounded-full border border-white/30" data-id="element-1990" data-mp="1o75zpoyr40nr" />
                <span className="text-white/90 text-sm font-medium" data-id="element-1991" data-mp="22czj2lrup8vb">
                  Norden helpt je door de wirwar van SEO heen
                </span>
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-1992" data-mp="1y9tb99p3b9mq">
                SEO dat werkt als systeem, niet als trucje
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="text-white/90 mb-10 text-xl leading-relaxed font-medium max-w-2xl" data-id="element-1993" data-mp="1td2ns1ayhbra">
                De meeste websites zijn onzichtbaar omdat de structuur
                ontbreekt. Wij bouwen geen losse blogs, we bouwen een schaalbaar
                systeem voor structurele groei.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex flex-wrap gap-4" data-id="element-1994" data-mp="3v039dt5dnedg">
                <a href="/prijsscan" className="inline-flex items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-1995" data-mp="mp8om8uafp04">
                  Start de scan
                  <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1996" data-mp="wipl5klt7i7h" />
                </a>
                <a href="/contact" className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-1997" data-mp="15h1f9wlvwy4o">
                  Plan een gesprek
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT SEO REALLY IS */}
      <section className="py-20 bg-white" data-id="element-1998" data-mp="1p0qmedsmwm4a">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1999" data-mp="1b43b15zxpteu">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2000" data-mp="1f4nc2knmph69">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2001" data-mp="1f6zyba0f6dp4">
              De harde waarheid over SEO
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-2002" data-mp="164qs2wc6p6hj">
              SEO is geen losse dienst die je even "aanzet". Het is een
              vermenigvuldiging van factoren. Als één van deze factoren een nul
              is, is het eindresultaat ook nul.
            </p>
          </div>

          <div className="bg-[#F7F8FA] rounded-2xl p-8 md:p-12 border border-gray-100 mb-12" data-id="element-2003" data-mp="ie4jn3y9pk70">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-xl md:text-2xl font-bold text-[#1B2B44] text-center flex-wrap" data-id="element-2004" data-mp="ne90iv338gxr">
              <span data-id="element-2005" data-mp="2p4znx4kcwpsp">Zichtbaarheid</span>
              <span className="text-[#007AFF]" data-id="element-2006" data-mp="2kboklder6r5j">=</span>
              <span className="flex items-center gap-2" data-id="element-2007" data-mp="2ka3o3i8voy51">
                <WrenchIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2008" data-mp="2fe52g2expu8b" /> Techniek
              </span>
              <span className="text-[#007AFF]" data-id="element-2009" data-mp="2bcb8ecz8voh4">×</span>
              <span className="flex items-center gap-2" data-id="element-2010" data-mp="2b9p02b4j54xu">
                <FileTextIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2011" data-mp="380hihfexr92s" /> Content
              </span>
              <span className="text-[#007AFF]" data-id="element-2012" data-mp="333zfol361kt3">×</span>
              <span className="flex items-center gap-2" data-id="element-2013" data-mp="2z12qucn18sgp">
                <ShieldCheckIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2014" data-mp="2yzjbqpd44ngi" />{' '}
                Autoriteit
              </span>
              <span className="text-[#007AFF]" data-id="element-2015" data-mp="2u44269m51dpw">×</span>
              <span className="flex items-center gap-2" data-id="element-2016" data-mp="1nmyxn8o9r8cp">
                <MousePointerClickIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2017" data-mp="1imugdgfje9lf" /> UX
              </span>
              <span className="text-[#007AFF]" data-id="element-2018" data-mp="3ua6e8qrlglrl">×</span>
              <span className="flex items-center gap-2" data-id="element-2019" data-mp="3ubhj6twrzzka">
                <Share2Icon className="w-6 h-6 text-[#007AFF]" data-id="element-2020" data-mp="33n1v7640nr6" /> Distributie
              </span>
            </div>
            <div className="mt-8 flex items-center justify-center gap-3" data-id="element-2021" data-mp="758c0kr9enxo">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2022" data-mp="bw6y60yxig8u" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2023" data-mp="gxnt7fuqf8hi">
                Norden legt het simpel uit: als één factor ontbreekt, valt het
                hele systeem.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-2024" data-mp="uuswj8329t92">
            {seoFactors.map((factor, idx) => <div key={idx} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-2025" data-mp="2slm52vsa51k1" data-mpi={idx}>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#007AFF] mb-4" data-id="element-2026" data-mp="2wmxup1b5lms8">
                  {factor.icon}
                </div>
                <h3 className="font-bold text-[#111827] mb-2" data-id="element-2027" data-mp="1x9mb8x13bv2r">
                  {factor.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2028" data-mp="2b941zhxy5ier">
                  {factor.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. SEO MATURITY MODEL */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-2029" data-mp="1ijouh25hq7nl">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2030" data-mp="1dng751h5385c">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2031" data-mp="1sbb26oimdoll">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2032" data-mp="1scvzglwbjln5">
              Het SEO Volwassenheidsmodel
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-2033" data-mp="zv09kg70c2bt">
              Groei in SEO gaat in fases. Je kunt geen dominantie verwachten als
              de fundering nog ontbreekt. Waar staat jouw kantoor?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2034" data-mp="36hzf8bxrski5">
            {maturityPhases.map((phase, idx) => <div key={idx} className={`rounded-2xl p-8 border-2 relative overflow-hidden ${phase.color}`} data-id="element-2035" data-mp="3bbk1x7twxs21" data-mpi={idx}>
                <div className="absolute top-0 right-0 p-4 opacity-20 font-black text-6xl" data-id="element-2036" data-mp="2xgrfdpflvbly">
                  {phase.levels}
                </div>
                <div className="relative z-10" data-id="element-2037" data-mp="3p9s5vzpd9tmp">
                  <span className="uppercase tracking-wider text-xs font-bold mb-2 block opacity-80" data-id="element-2038" data-mp="3p7xjegyg5tgi">
                    Fase {idx + 1}: {phase.phase}
                  </span>
                  <h3 className="text-2xl font-bold mb-6" data-id="element-2039" data-mp="3g8kb3rm27axd">{phase.title}</h3>

                  <div className="space-y-6" data-id="element-2040" data-mp="3l7ndt5ekh526">
                    <div data-id="element-2041" data-mp="3lajbcsmi5rq3">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 opacity-90" data-id="element-2042" data-mp="24uggcj7ndir9">
                        <CheckCircleIcon className="w-4 h-4" data-id="element-2043" data-mp="29t9v0eyo7ft0" /> Wat gebeurt er
                        nu?
                      </h4>
                      <p className="text-sm opacity-80 leading-relaxed" data-id="element-2044" data-mp="fc00ux4xti15">
                        {phase.happening}
                      </p>
                    </div>
                    <div data-id="element-2045" data-mp="5n8ct02zzhvi">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 opacity-90" data-id="element-2046" data-mp="5mgg1lxnze3n">
                        <ZapIcon className="w-4 h-4" data-id="element-2047" data-mp="1mg3y7y1fezr" /> Wat ontbreekt er?
                      </h4>
                      <p className="text-sm opacity-80 leading-relaxed" data-id="element-2048" data-mp="3ss7rr0ocefje">
                        {phase.missing}
                      </p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3.5 WAAROM SEO VAAK NIET WERKT */}
      <section className="py-24 bg-white" data-id="element-2049" data-mp="3et82prwx3ymp">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2050" data-mp="3euj5ppijh2cr">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2051" data-mp="1h1lwdsqpxb5q">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2052" data-mp="1d1uzr8eig0ta">
              Waarom SEO bij 90% van de bedrijven niet werkt
            </h2>
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-2053" data-mp="2uwluhnp9xy36">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2054" data-mp="2zvou2smhjc3r" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2055" data-mp="38pbke37xbn5k">
                Norden ziet het keer op keer: bedrijven investeren in SEO, maar
                missen de structuur om het te laten werken.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-2056" data-mp="2l0ilbxw08z6q">
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2057" data-mp="2pzbz7rf7es7f">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2058" data-mp="2px910k3wdsrm">
                <AlertTriangleIcon className="w-5 h-5" data-id="element-2059" data-mp="594gpwpidpws" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2060" data-mp="579xqnxss7vc">
                1. Losse blogs zonder structuur
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2061" data-mp="382u8jnrka5x9">
                Willekeurige artikelen schrijven zonder dat ze onderling
                verbonden zijn. Google snapt de context niet.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2062" data-mp="3h2h6u0ozuvm3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2063" data-mp="3vrn5nnjx6jtg">
                <TargetIcon className="w-5 h-5" data-id="element-2064" data-mp="3qsa9udb2m2eq" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2065" data-mp="8ngpa0ndr5cm">
                2. Verkeerde zoekintentie
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2066" data-mp="4lveckuep152">
                Proberen te ranken met een verkooppagina op een zoekterm waarbij
                de gebruiker eigenlijk informatie zoekt.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2067" data-mp="26fl17n24tje6">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2068" data-mp="1wa38qkrzn7qj">
                <RefreshCwIcon className="w-5 h-5" data-id="element-2069" data-mp="20d9cnlrmomtu" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2070" data-mp="1miywvtxm02zx">
                3. Te snel opgeven
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2071" data-mp="1rh0dm87kbq88">
                Stoppen na 3 maanden omdat er nog geen leads zijn, precies
                wanneer het vliegwiel op gang begint te komen.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2072" data-mp="13l5qghns1axs">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2073" data-mp="13n8sk027euiv">
                <CodeIcon className="w-5 h-5" data-id="element-2074" data-mp="2xz6q34r5i1yx" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2075" data-mp="2xwua2x4ye1oi">
                4. Techniek zonder content
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2076" data-mp="8ka3gu6mhdf8">
                Een razendsnelle website hebben, maar geen antwoord geven op de
                vragen van je doelgroep.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2077" data-mp="ii6b3c0cpq8l">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2078" data-mp="n8wguqcovv3t">
                <NetworkIcon className="w-5 h-5" data-id="element-2079" data-mp="rb9o5j73412p" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2080" data-mp="w9um1byv68nz">
                5. Geen interne linking
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2081" data-mp="118e9xhx0puiz">
                Pagina's die nergens naar verwijzen. De SEO-waarde stroomt niet
                door de website heen.
              </p>
            </div>
          </div>

          <div className="text-center" data-id="element-2082" data-mp="331vegcpuqf9i">
            <a href="/seo/blog/waarom-werkt-seo-vaak-niet" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group" data-id="element-2083" data-mp="37zdqwd32vcm9">
              Lees de uitgebreide analyse waarom SEO vaak faalt
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2084" data-mp="1kdas3hfi8cq2" />
            </a>
          </div>
        </div>
      </section>

      {/* 4. SEO SILO (MOST IMPORTANT) */}
      <section className="py-32 bg-[#1B2B44] text-white relative overflow-hidden" data-id="element-2085" data-mp="1bdxd698m2vxy">
        {/* Decorative background lines */}
        <div className="absolute inset-0 opacity-5" data-id="element-2086" data-mp="282c1p802ptwx">
          <div className="absolute top-0 left-1/4 w-px h-full bg-white" data-id="element-2087" data-mp="241a1anuf7btd"></div>
          <div className="absolute top-0 left-2/4 w-px h-full bg-white" data-id="element-2088" data-mp="1z2goyyqvdqus"></div>
          <div className="absolute top-0 left-3/4 w-px h-full bg-white" data-id="element-2089" data-mp="1z4jj9uz2265b"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-2090" data-mp="1u5gg6g2my9zz">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-2091" data-mp="nol8nmo8bc1z">
            <div className="inline-flex items-center gap-3 bg-white/10 px-4 py-2 rounded-full border border-white/20 mb-6" data-id="element-2092" data-mp="in5l9nykwtgf">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full" data-id="element-2093" data-mp="k5vxl0c18n0n" />
              <span className="text-white text-sm font-bold tracking-wide uppercase" data-id="element-2094" data-mp="b5h7lno4ijqt">
                Onze kernstrategie
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-2095" data-mp="g5lovfwuvii3">
              De SEO Silo: Waarom structuur alles bepaalt
            </h2>

            <p className="text-xl text-white/80 leading-relaxed mb-8" data-id="element-2096" data-mp="66mmug6lil42">
              Wij bouwen een <strong data-id="element-2097" data-mp="3okdjswcjop36">SEO Silo structuur</strong>. We groeperen
              gerelateerde content in strakke hiërarchieën (Home → Categorie →
              Detail). Door slimme interne linking vloeit de SEO-waarde door je
              hele site. Dit is het geheim achter schaalbare groei en ware
              autoriteit.
            </p>

            <div className="flex items-center justify-center gap-3 mb-8" data-id="element-2098" data-mp="3jjpqu54r9y6b">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-8 h-8 rounded-full border-2 border-white/30" data-id="element-2099" data-mp="2zzh5ah7ke3xf" />
              <p className="text-base italic text-white/70" data-id="element-2100" data-mp="39xwxknibvj8i">
                "Norden helpt hier structuur aan te brengen. Dit is waar de
                meeste websites falen, en waar wij het verschil maken."
              </p>
            </div>

            <a href="/seo/blog/seo-silo-wat-is-het" className="inline-flex items-center bg-[#007AFF] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#005FCC] transition-colors group" data-id="element-2101" data-mp="3ivgzlyo0ljk6">
              Lees alles over de SEO Silo structuur
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2102" data-mp="3nnqpxft1klmz" />
            </a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-2103" data-mp="5j6ukoiknrpi">
            {/* Zonder Silo */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-2104" data-mp="1ez70unuohsv">
              <h3 className="text-2xl font-bold mb-6 text-red-400 flex items-center gap-3" data-id="element-2105" data-mp="1uz9g652c8m9n">
                <AlertTriangleIcon className="w-6 h-6" data-id="element-2106" data-mp="1zxb00s7l8pm6" /> Zonder Silo (Chaos)
              </h3>
              <ul className="space-y-4 text-white/80" data-id="element-2107" data-mp="24qx25qg4lb3q">
                <li className="flex items-start gap-3" data-id="element-2108" data-mp="29ofdfnhm82ew">
                  <span className="text-red-400 mt-1" data-id="element-2109" data-mp="29nvv45o1zc0c">✗</span>
                  Pagina's staan los van elkaar, Google snapt de context niet.
                </li>
                <li className="flex items-start gap-3" data-id="element-2110" data-mp="1gz86q180c6ik">
                  <span className="text-red-400 mt-1" data-id="element-2111" data-mp="1h1unmrgsfydg">✗</span>
                  SEO-waarde (link juice) stroomt niet door de website.
                </li>
                <li className="flex items-start gap-3" data-id="element-2112" data-mp="1q1r3q4p43oww">
                  <span className="text-red-400 mt-1" data-id="element-2113" data-mp="2xe9pi2u41my3">✗</span>
                  Bezoekers verdwalen en verlaten de site snel.
                </li>
                <li className="flex items-start gap-3" data-id="element-2114" data-mp="31ejupda2w9bw">
                  <span className="text-red-400 mt-1" data-id="element-2115" data-mp="ps9nhfriss8k">✗</span>
                  Je concurreert met je eigen pagina's (keyword
                  cannibalization).
                </li>
              </ul>
            </div>

            {/* Met Silo */}
            <div className="bg-gradient-to-br from-[#007AFF]/20 to-transparent border border-[#007AFF]/30 rounded-2xl p-8" data-id="element-2116" data-mp="gywj9ho0a476">
              <h3 className="text-2xl font-bold mb-6 text-[#007AFF] flex items-center gap-3" data-id="element-2117" data-mp="bz0g1kn4ugso">
                <CheckCircleIcon className="w-6 h-6" data-id="element-2118" data-mp="6zxb0075kakp" /> Met Silo (Groei)
              </h3>
              <ul className="space-y-4 text-white/90" data-id="element-2119" data-mp="21m0rocy3y1y">
                <li className="flex items-start gap-3" data-id="element-2120" data-mp="3u67va51lco1t">
                  <span className="text-[#007AFF] mt-1" data-id="element-2121" data-mp="3u8u1a08u2vhx">✓</span>
                  Strakke hiërarchie: Google ziet jou als dé autoriteit.
                </li>
                <li className="flex items-start gap-3" data-id="element-2122" data-mp="1sebjqvffbe71">
                  <span className="text-[#007AFF] mt-1" data-id="element-2123" data-mp="1ng9z462cn6tg">✓</span>
                  Eén sterke pagina trekt de hele cluster mee omhoog.
                </li>
                <li className="flex items-start gap-3" data-id="element-2124" data-mp="26k3fh2lz625u">
                  <span className="text-[#007AFF] mt-1" data-id="element-2125" data-mp="2bbd35xnyqvf3">✓</span>
                  Logische navigatie houdt bezoekers langer vast.
                </li>
                <li className="flex items-start gap-3" data-id="element-2126" data-mp="1xlrr778kzggk">
                  <span className="text-[#007AFF] mt-1" data-id="element-2127" data-mp="22iryflh62p99">✓</span>
                  Schaalbaar: nieuwe content voegt direct waarde toe.
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white/5 rounded-3xl p-4 sm:p-6 md:p-10 border border-white/10 max-w-4xl mx-auto" data-id="element-2128" data-mp="2uaqzund2uk9a">
            <div className="flex flex-col items-center" data-id="element-2129" data-mp="2g9myc89otw0p">
              {/* Home */}
              <div className="bg-[#007AFF] text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-xl mb-8 relative z-10 shadow-lg border border-white/20" data-id="element-2130" data-mp="2l99jd3vrcgk2">
                Home
              </div>

              {/* Lines from Home to Categories */}
              <div className="w-full max-w-2xl h-px bg-white/20 relative -mt-4 mb-8 z-0" data-id="element-2131" data-mp="12b39n4lma68c">
                <div className="absolute left-1/2 top-0 w-px h-8 bg-white/20 -translate-y-full" data-id="element-2132" data-mp="12cxw4ncje6ej"></div>
                <div className="absolute left-0 top-0 w-px h-8 bg-white/20" data-id="element-2133" data-mp="xecssf1zn6lv"></div>
                <div className="absolute left-1/2 top-0 w-px h-8 bg-white/20" data-id="element-2134" data-mp="1bb01tg77tn85"></div>
                <div className="absolute right-0 top-0 w-px h-8 bg-white/20" data-id="element-2135" data-mp="16d7zrtcuwkq8"></div>
              </div>

              {/* Categories */}
              <div className="w-full max-w-3xl flex justify-between px-0 sm:px-2 md:px-4 mb-12 relative z-10" data-id="element-2136" data-mp="eliqakljx6yj">
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-2137" data-mp="scnjb3uez0zw">
                  Hypotheken
                </div>
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-2138" data-mp="3w18p8jboil27">
                  Pensioenen
                </div>
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-2139" data-mp="36i5cz9ctbpnw">
                  Verzekeringen
                </div>
              </div>

              {/* Lines from Categories to Details */}
              <div className="w-full max-w-3xl flex justify-between px-3 sm:px-6 md:px-10 relative -mt-6 mb-6 z-0" data-id="element-2140" data-mp="3khwnv1818pxo">
                <div className="w-px h-6 bg-white/20" data-id="element-2141" data-mp="3pfgez1ptwx71"></div>
                <div className="w-px h-6 bg-white/20" data-id="element-2142" data-mp="3th1m06fomh9b"></div>
                <div className="w-px h-6 bg-white/20" data-id="element-2143" data-mp="3tfgs8j16z2ci"></div>
              </div>

              {/* Detail pages (Clusters) */}
              <div className="w-full max-w-3xl flex justify-between px-0 sm:px-2" data-id="element-2144" data-mp="71pam0hjvqxw">
                <div className="flex flex-col gap-3" data-id="element-2145" data-mp="73tb08g8q7tf">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2146" data-mp="c2czafib3voy">
                    Oversluiten
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2147" data-mp="21jick677ywq2">
                    Starters
                  </div>
                </div>
                <div className="flex flex-col gap-3" data-id="element-2148" data-mp="1ml6f3xc0vjxu">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2149" data-mp="1mmr8hjmlowu4">
                    Lijfrente
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2150" data-mp="2fby70im21mfd">
                    ZZP
                  </div>
                </div>
                <div className="flex flex-col gap-3" data-id="element-2151" data-mp="32zpr48du73r7">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2152" data-mp="3319cuei9i70d">
                    AOV
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2153" data-mp="xiy2mt5bx0yv">
                    Zakelijk
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 border-t border-white/10 pt-12" data-id="element-2154" data-mp="eqc9whppxqii">
            <div className="text-center" data-id="element-2155" data-mp="9qzm9ur0wfev">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-2156" data-mp="9pxzit0na4hb">3x</div>
              <p className="text-white/80 font-medium" data-id="element-2157" data-mp="4th89py5fa00">
                Meer organisch verkeer
              </p>
            </div>
            <div className="text-center" data-id="element-2158" data-mp="3w0k0mkpq3mbt">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-2159" data-mp="1z6z19shboz2f">Top</div>
              <p className="text-white/80 font-medium" data-id="element-2160" data-mp="1u7cofk5epku5">
                Hogere autoriteit per pagina
              </p>
            </div>
            <div className="text-center" data-id="element-2161" data-mp="22p66cqh21vin">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-2162" data-mp="22mabxc4f1oyy">∞</div>
              <p className="text-white/80 font-medium" data-id="element-2163" data-mp="26of220vno3gu">Schaalbare groei</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. OVERVIEW OF SEO COMPONENTS */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-2164" data-mp="2geq5lmbfgan7">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2165" data-mp="2ghm53f2xb7dr">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2166" data-mp="1jrbs9y12ktte">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2167" data-mp="1nqcapffmxufv">
              De componenten van ons systeem
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed mb-6" data-id="element-2168" data-mp="1ss9xgjn8v7dk">
              Niet alles is altijd nodig. Het hangt af van waar je nu staat in
              je SEO-reis. Hieronder vind je alle onderdelen van ons systeem.
              Klik op een component om te zien wat het inhoudt en waarom het
              belangrijk is.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-2169" data-mp="2jfi7kppoen14">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2170" data-mp="2eh8c0e01hkez" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2171" data-mp="2tc4t7gptti47">
                Norden helpt je bepalen welke componenten prioriteit hebben.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2172" data-mp="20npfqymcf7uz">
            {seoComponents.map(comp => <div key={comp.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-2173" data-mp="1vsjttzsxxt6w">
                <button onClick={() => setExpandedComponent(expandedComponent === comp.id ? null : comp.id)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2174" data-mp="25od41cj6tali">
                  <div className="flex items-center gap-4" data-id="element-2175" data-mp="1d10ty1u7qv93">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-[#007AFF]" data-id="element-2176" data-mp="1cyeme27bofqv">
                      {comp.icon}
                    </div>
                    <h3 className="font-bold text-[#111827] text-lg" data-id="element-2177" data-mp="3tfm1v4fc6261">
                      {comp.title}
                    </h3>
                  </div>
                  {expandedComponent === comp.id ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-2178" data-mp="3jp9gj9zp64yu" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-2179" data-mp="b8xybizxpn2h" />}
                </button>

                <AnimatePresence data-id="element-2180" data-mp="g7iyxjj3m8rg">
                  {expandedComponent === comp.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2181" data-mp="g5oox09d8125">
                      <div className="px-6 pb-6 pt-2 border-t border-gray-50" data-id="element-2182" data-mp="2n0lf9pc7awc4">
                        <p className="text-[#111827] font-medium mb-4" data-id="element-2183" data-mp="2r15b8pl1ftwo">
                          {comp.explanation}
                        </p>

                        <div className="space-y-4 mb-6" data-id="element-2184" data-mp="32giaa473e4rw">
                          <div data-id="element-2185" data-mp="32exfqeks2lu1">
                            <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF] block mb-1" data-id="element-2186" data-mp="2xgvzs2inf2ms">
                              Wat we doen
                            </span>
                            <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2187" data-mp="3q5t411q0yywj">
                              {comp.happens}
                            </p>
                          </div>
                          <div data-id="element-2188" data-mp="3l4x0ip9l5ern">
                            <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF] block mb-1" data-id="element-2189" data-mp="3g6vm4hn3u3mi">
                              Waarom het werkt
                            </span>
                            <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2190" data-mp="24sqffgvibfnh">
                              {comp.matters}
                            </p>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-2191" data-mp="ga5e7d8y38qk">
                          <p className="text-sm text-gray-600 mb-2" data-id="element-2192" data-mp="kbh7ptuxwe41">
                            Wil je hier dieper in duiken?
                          </p>
                          <a href={comp.blogLink} className="text-sm font-semibold text-[#007AFF] hover:underline flex items-center" data-id="element-2193" data-mp="u08wxu8m8kb9">
                            Lees: {comp.blogTitle}{' '}
                            <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-2194" data-mp="z1oevdfhyapf" />
                          </a>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. PROGRAMMATIC SEO & 7. SEARCH EVERYWHERE */}
      <section className="py-20 bg-white" data-id="element-2195" data-mp="39s6k9ecl6m0a">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2196" data-mp="39thmh9qdvlpa">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2197" data-mp="3eqzvt18bc2xt">
            {/* Programmatic SEO */}
            <div className="bg-[#1B2B44] rounded-2xl p-8 md:p-10 text-white relative overflow-hidden" data-id="element-2198" data-mp="lw9h8dyopmx0">
              <div className="absolute top-0 right-0 opacity-10 transform translate-x-1/4 -translate-y-1/4" data-id="element-2199" data-mp="gyr3ma9q2jio">
                <LayersIcon className="w-64 h-64" data-id="element-2200" data-mp="uul5488626lr" />
              </div>
              <div className="relative z-10" data-id="element-2201" data-mp="pwtah6g506dv">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-3" data-id="element-2202" data-mp="14uvibsu7be0x">
                  <DatabaseIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2203" data-mp="14sr7ss1srgro" />
                  Programmatic SEO & Schalen
                </h3>
                <p className="text-white/80 leading-relaxed mb-6" data-id="element-2204" data-mp="1dnozjvt7knhf">
                  Zodra de basis staat, is het tijd om te schalen. Met
                  programmatic SEO genereren we op een slimme, kwalitatieve
                  manier tientallen tot honderden geoptimaliseerde pagina's.
                </p>
                <ul className="space-y-3 mb-8" data-id="element-2205" data-mp="1in1p4obgs8np">
                  <li className="flex items-start gap-3" data-id="element-2206" data-mp="1ik5qsyvpfhyq">
                    <CheckCircleIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" data-id="element-2207" data-mp="2293j9x2twx78" />
                    <span className="text-white/90" data-id="element-2208" data-mp="2763o64nxztwr">
                      <strong data-id="element-2209" data-mp="274sl672bmq6p">City pages:</strong> "Hypotheekadviseur [Stad]"
                      voor regionale dominantie.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-2210" data-mp="1x74ecirfxs86">
                    <CheckCircleIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" data-id="element-2211" data-mp="1jhaobx4791wv" />
                    <span className="text-white/90" data-id="element-2212" data-mp="1o8sncfmnwsf7">
                      <strong data-id="element-2213" data-mp="1oadk8bwg8ng8">Niche pages:</strong> Specifieke oplossingen voor
                      specifieke doelgroepen.
                    </span>
                  </li>
                </ul>
                <a href="/seo/blog/wat-is-programmatic-seo" className="inline-flex items-center text-white font-semibold hover:text-[#007AFF] transition-colors group" data-id="element-2214" data-mp="1eir17tdvdk2x">
                  Lees hoe we schalen naar 100+ pagina's
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2215" data-mp="1kzsrz1pa1si1" />
                </a>
              </div>
            </div>

            {/* Search Everywhere */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-10 border border-blue-100" data-id="element-2216" data-mp="1b1mrmoj4cufa">
              <h3 className="text-2xl font-bold text-[#111827] mb-4 flex items-center gap-3" data-id="element-2217" data-mp="163biyh4k8pyn">
                <GlobeIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2218" data-mp="121gljnpr2wq5" />
                Search is overal
              </h3>
              <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-2219" data-mp="x96wsb0dg2pg">
                Mensen zoeken allang niet meer alleen via Google. Een modern SEO
                systeem zorgt dat je overal vindbaar bent waar jouw doelgroep
                zoekt.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2220" data-mp="icxx58ckjnci">
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2221" data-mp="ic64a5acw3px">
                  <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-2222" data-mp="edgqwcpbim6a" />
                  <span className="font-semibold text-gray-800" data-id="element-2223" data-mp="2pxxul3sk7z5u">Google</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2224" data-mp="2tyztfjike97a">
                  <NetworkIcon className="w-5 h-5 text-blue-600" data-id="element-2225" data-mp="2z072rn0cvgg2" />
                  <span className="font-semibold text-gray-800" data-id="element-2226" data-mp="33yqnxl74ko7d">LinkedIn</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2227" data-mp="26brq374p5a7w">
                  <PlayCircleIcon className="w-5 h-5 text-red-500" data-id="element-2228" data-mp="2bal6b7bdbfbr" />
                  <span className="font-semibold text-gray-800" data-id="element-2229" data-mp="2g8e9drhdjt7t">YouTube</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2230" data-mp="2l04qqheqbh8q">
                  <BrainIcon className="w-5 h-5 text-purple-500" data-id="element-2231" data-mp="1nj7dz1d7vjqs" />
                  <span className="font-semibold text-gray-800" data-id="element-2232" data-mp="1sj22iy9irx8o">AI Tools</span>
                </div>
              </div>
              <a href="/seo/blog/seo-voor-ai-tools" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group" data-id="element-2233" data-mp="1294v285j4tsb">
                Lees meer over SEO voor AI tools
                <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2234" data-mp="t7on0726t3r9" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8. VISUAL FORMULAS */}
      <section className="py-20 bg-[#111827] text-white" data-id="element-2235" data-mp="jhc4ekdxnknr">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2236" data-mp="oa58d61kuoys">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2237" data-mp="9kqzdz1wy9jx">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2238" data-mp="9mk5nd8m6rre">
              De wiskunde achter online succes
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-6" data-id="element-2239" data-mp="k2hgt9efp1r">
              SEO is geen magie. Het is een logisch gevolg van de juiste
              variabelen.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-2240" data-mp="5kykl4lxf764">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-600" data-id="element-2241" data-mp="3i39q5l9hdrai" />
              <p className="text-sm italic text-gray-400" data-id="element-2242" data-mp="3r4h98w3felk0">
                Norden rekent het voor je uit.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 max-w-4xl mx-auto" data-id="element-2243" data-mp="4w7xw3qckid4">
            {formulas.map((item, idx) => <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:bg-white/10 transition-colors" data-id="element-2244" data-mp="3w2iv1os8kyz6" data-mpi={idx}>
                <span className="text-gray-400 font-medium text-sm uppercase tracking-wider" data-id="element-2245" data-mp="9my2heqy8h6r">
                  {item.title}
                </span>
                <span className="text-xl md:text-2xl font-bold text-white font-mono tracking-tight" data-id="element-2246" data-mp="nnhawmzbtp9g">
                  {item.formula}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. FAQ SECTION */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-2247" data-mp="3hep43s7tq3zp">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px]" data-id="element-2248" data-mp="3hfh0h598w5r1">
          <div className="text-center mb-12" data-id="element-2249" data-mp="3lg0sjueyoj6b">
            <h2 className="text-3xl font-bold text-[#111827] mb-4" data-id="element-2250" data-mp="3le69ymr5x35e">
              Veelgestelde vragen
            </h2>
            <p className="text-[#6B7280] mb-6" data-id="element-2251" data-mp="3v62i6qmyq9nc">
              Eerlijke antwoorden op de meest gestelde vragen over SEO.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-2252" data-mp="3yq0mai63u6w">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2253" data-mp="8z5d6zumkxnt" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2254" data-mp="cy4n6yv7rkm4">
                De vragen die Norden het vaakst hoort van financieel adviseurs.
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-2255" data-mp="2etnhhildmr4a">
            {faqs.map((faq, idx) => <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2256" data-mp="27932rl3s1cm0" data-mpi={idx}>
                <button onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2257" data-mp="22gt85rrpvode">
                  <span className="font-bold text-[#111827] pr-8" data-id="element-2258" data-mp="1thy0eb90y67z">{faq.q}</span>
                  {expandedFaq === idx ? <ChevronUpIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0" data-id="element-2259" data-mp="2q64cfj8a00wa" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-2260" data-mp="2l8c9lu63eudb" />}
                </button>
                <AnimatePresence data-id="element-2261" data-mp="2h5zdlxh5eatq">
                  {expandedFaq === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2262" data-mp="2h8lkduw7smaw">
                      <div className="px-6 pb-6 pt-2 text-[#6B7280] leading-relaxed border-t border-gray-50" data-id="element-2263" data-mp="10ulocv0hpgsx">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9.5 BRIDGE SECTION */}
      <section className="py-24 bg-gradient-to-r from-[#007AFF] to-[#005FCC] text-white" data-id="element-2264" data-mp="2jpcb9xe03xmc">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px] text-center" data-id="element-2265" data-mp="37foi14xifobc">
          <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full border-2 border-white/30 mx-auto mb-6" data-id="element-2266" data-mp="3ce9hh24xu3yq" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2267" data-mp="33dl2a43t5xk9">
            De vraag is niet óf je SEO nodig hebt, maar waar je nu staat.
          </h2>
          <p className="text-xl text-white/80 mb-10" data-id="element-2268" data-mp="1my0bbdhux73w">
            Doe de scan en ontdek in 2 minuten waar jouw grootste groeikansen
            liggen.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2269" data-mp="1jgw457dv7dd3">
            <a href="/prijsscan" className="inline-flex justify-center items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-2270" data-mp="1fg2cq417rgfg">
              Start de SEO scan
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2271" data-mp="1ohs6hgikirge" />
            </a>
            <a href="/contact" className="inline-flex justify-center items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-2272" data-mp="10rph5861fzlh">
              Plan een strategiegesprek
            </a>
          </div>
        </div>
      </section>

      {/* 10. BLOG ARTICLES OVERVIEW */}
      <section className="py-24 bg-white" data-id="element-2273" data-mp="i0esyyrp60xa">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2274" data-mp="hzdfyor4f4cx">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2275" data-mp="d2d9icqczzla">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2276" data-mp="2d3upuv39cegl">
              Duik dieper in de materie
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-2277" data-mp="2i1w6l9d7o1ow">
              Onze complete kennisbank. Geen oppervlakkige tips, maar diepgaande
              strategische artikelen over hoe SEO écht werkt.
            </p>
          </div>

          {/* Featured Article */}
          <div className="mb-16 bg-[#F7F8FA] rounded-3xl p-8 md:p-12 border border-gray-200" data-id="element-2278" data-mp="1u58pjifkerdq">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center" data-id="element-2279" data-mp="1z3s95c6orr2x">
              <div data-id="element-2280" data-mp="1z5wlmiinhyet">
                <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-bold rounded-full mb-4 uppercase tracking-wider" data-id="element-2281" data-mp="2355hpu15y8dj">
                  Cornerstone Artikel
                </span>
                <h3 className="text-3xl font-bold text-[#111827] mb-4" data-id="element-2282" data-mp="3elc0xi12xlm9">
                  Wat is een SEO silo en hoe werkt het?
                </h3>
                <p className="text-[#6B7280] text-lg leading-relaxed mb-8" data-id="element-2283" data-mp="3ji2fu215eb63">
                  Dit is het belangrijkste artikel in onze kennisbank. Ontdek
                  waarom 90% van de websites faalt in Google en hoe je met een
                  strakke silo-structuur de concurrentie domineert.
                </p>
                <a href="/seo/blog/seo-silo-wat-is-het" className="inline-flex items-center bg-[#1B2B44] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#111827] transition-colors group" data-id="element-2284" data-mp="3fwfbe9zb68n3">
                  Lees het cornerstone artikel
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2285" data-mp="35x7w3h90k3xc" />
                </a>
              </div>
              <div className="relative hidden lg:block" data-id="element-2286" data-mp="310zocox5bd07">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#007AFF]/20 to-transparent rounded-2xl transform rotate-3" data-id="element-2287" data-mp="2wywajtn07wbr"></div>
                <div className="bg-white p-8 rounded-2xl border border-gray-200 relative z-10 shadow-lg" data-id="element-2288" data-mp="z5gxyga3sqnb">
                  <div className="w-full h-40 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center mb-6" data-id="element-2289" data-mp="v4deljsukchc">
                    <NetworkIcon className="w-12 h-12 text-gray-300" data-id="element-2290" data-mp="1ifi6yp3ovjjl" />
                  </div>
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-3" data-id="element-2291" data-mp="14o3lmbmb769d"></div>
                  <div className="h-4 bg-gray-100 rounded w-1/2" data-id="element-2292" data-mp="19npxohqeiggl"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Clusters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2293" data-mp="utbm329629by">
            {blogClusters.map((cluster, idx) => <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2294" data-mp="lqk92y0n75i7" data-mpi={idx}>
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2295" data-mp="qq6kr30todow">
                  <h3 className="text-lg font-bold text-[#1B2B44] flex items-center gap-2" data-id="element-2296" data-mp="2g5mt4g6x9vdq">
                    <span className="w-6 h-6 rounded-full bg-blue-50 text-[#007AFF] flex items-center justify-center text-xs" data-id="element-2297" data-mp="2l23i1cly4drv">
                      {idx + 1}
                    </span>
                    {cluster.title}
                  </h3>
                  <span className="text-xs font-semibold text-gray-400 bg-gray-50 px-2 py-1 rounded-md" data-id="element-2298" data-mp="2q6feo874um1r">
                    {cluster.articles.length} artikelen
                  </span>
                </div>
                <ul className="space-y-3" data-id="element-2299" data-mp="3dxuiy6igzdfn">
                  {cluster.articles.map((article, aIdx) => <li key={aIdx} data-id="element-2300" data-mp="3dznt3vsakfse" data-mpi={aIdx}>
                      <a href={`/seo/blog/${article.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug group" data-id="element-2301" data-mp="38z9m8h2ffbvy">
                        <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-300 group-hover:text-[#007AFF] transition-colors" data-id="element-2302" data-mp="3iwxpxwi0ftq9" />
                        {article.title}
                      </a>
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Meer artikelen CTA */}
          <div className="mt-16 text-center" data-id="element-2303" data-mp="vw68eadxj7f3">
            <a href="/bibliotheek" className="inline-flex items-center bg-white border-2 border-[#1B2B44] text-[#1B2B44] hover:bg-[#1B2B44] hover:text-white font-bold py-4 px-8 rounded-lg transition duration-300 shadow-sm hover:shadow-md group" data-id="element-2304" data-mp="vv3fjwx1quwj">
              Bekijk alle artikelen in de Bibliotheek
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2305" data-mp="10pr9wceqejj1" />
            </a>
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="py-24 bg-gradient-to-br from-[#007AFF] to-[#1B2B44] relative overflow-hidden" data-id="element-2306" data-mp="80trm9aismqa">
        <div className="absolute inset-0 opacity-10" data-id="element-2307" data-mp="lwnpzydo3tp5">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2308" data-mp="hwx1y2fizrob"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px] relative z-10 text-center" data-id="element-2309" data-mp="1td3ifio24qtc">
          <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte" className="w-24 h-24 rounded-full border-4 border-white/20 mx-auto mb-8 shadow-xl" data-id="element-2310" data-mp="20utcbniqeiho" />

          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-2311" data-mp="1qz85nc2pwu1l">
            SEO is geen losse dienst. Het is een systeem dat moet kloppen.
          </h2>

          <p className="text-xl text-white/80 mb-10 leading-relaxed" data-id="element-2312" data-mp="2jndr611lin2h">
            Klaar om te stoppen met losse flodders en te starten met structurele
            groei? Laten we kijken waar jouw kansen liggen.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2313" data-mp="29vztzzx49zci">
            <a href="/prijsscan" className="inline-flex justify-center items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-2314" data-mp="2eukn7fdybirg">
              Start de scan
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2315" data-mp="37d822d2cu1k3" />
            </a>
            <a href="/contact" className="inline-flex justify-center items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-2316" data-mp="3h5jdyyo6rbwd">
              Plan een gesprek
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-2317" data-mp="33agx5ghsw24s" />
    </div>;
};